import { TrashIcon } from '@heroicons/react/24/outline';
import type { DataTableColumn } from '@odo/shared-ui';
import { RowMenu, type RowMenuItem } from '../../../billing/RowMenu';
import type { Communication, CommunicationLastEntry } from '../../../../services/communications';
import { formatRelativeActivity } from '../../../../utils/dates';
import { CategoryPillsCapped } from './CategoryPillsCapped';
import { channelDef } from './channels';
import { CommunicationAssigneeCell } from './CommunicationAssigneeCell';
import { CommunicationStatusBadge } from './CommunicationStatusBadge';
import { DIRECTION_LABEL } from './communicationFormatting';
import { EntriesCell } from './EntriesCell';
import { ReferenceInline } from './ReferenceInline';
import { communicationColWidths } from './communicationColumnWidths';

// Rows this close to the bottom open their assignee menu upwards - the table
// body clips its overflow, so a downward menu there would be cut off.
const DROP_UP_ROWS = 3;

// One sortable column today - the wire's `sort` value doubles as the
// column's `sortKey`, so no translation happens between them.
export type CommunicationsSortBy = 'lastActivityAt';

const TD_PADDING = 'px-3 first:pl-4 last:pr-3';
const TH_PADDING = 'px-3 first:pl-4 last:pr-3';
const TD_CLIP = `${TD_PADDING} overflow-hidden`;

interface BuildCommunicationColumnsArgs {
    communications: readonly Communication[];
    showPractice: boolean;
    onDelete: (communication: Communication) => void;
}

function menuItemsFor(
    communication: Communication,
    onDelete: (communication: Communication) => void
): RowMenuItem[] {
    return [
        {
            label: 'Delete communication',
            icon: <TrashIcon className="h-4 w-4" />,
            tone: 'danger',
            onClick: () => onDelete(communication)
        }
    ];
}

// Fallback for entries with neither title nor body, e.g. "Outgoing call".
function fallbackPreview(lastEntry: CommunicationLastEntry): string {
    const def = channelDef(lastEntry.channel);
    if (!lastEntry.direction) return def.label;
    return `${DIRECTION_LABEL[lastEntry.direction]} ${def.label.toLowerCase()}`;
}

// Column order and keys must stay in lockstep with `communicationColumnWidths.ts`.
export function buildCommunicationColumns({
    communications,
    showPractice,
    onDelete
}: BuildCommunicationColumnsArgs): DataTableColumn<Communication, CommunicationsSortBy>[] {
    const widths = communicationColWidths(showPractice);

    return [
        {
            key: 'reference',
            label: 'Reference',
            width: widths.reference,
            cellClassName: `${TD_CLIP} py-4 whitespace-nowrap`,
            headerClassName: TH_PADDING,
            render: (communication) => <ReferenceInline referenceNo={communication.referenceNo} />
        },
        {
            key: 'practice',
            label: 'Practice',
            width: widths.practice,
            visible: showPractice,
            cellClassName: `${TD_PADDING} py-4`,
            headerClassName: TH_PADDING,
            getTitle: (communication) => communication.practiceName ?? undefined,
            render: (communication) => (
                <p className="text-[13px] font-semibold text-gray-800 truncate">
                    {communication.practiceName ?? (
                        <span className="text-gray-400 font-normal italic">No practice</span>
                    )}
                </p>
            )
        },
        {
            key: 'title',
            label: 'Title',
            width: widths.title,
            cellClassName: `${TD_PADDING} py-4 overflow-hidden`,
            headerClassName: TH_PADDING,
            getTitle: (communication) =>
                communication.lastEntry
                    ? `${communication.title}\n${communication.lastEntry.snippet ?? fallbackPreview(communication.lastEntry)}`
                    : communication.title,
            render: (communication) => {
                const lastEntry = communication.lastEntry;
                const preview = lastEntry
                    ? (lastEntry.snippet ?? fallbackPreview(lastEntry))
                    : null;
                const previewDef = lastEntry ? channelDef(lastEntry.channel) : null;
                const PreviewIcon = previewDef?.icon;
                return (
                    <>
                        <p className="text-[13px] font-semibold text-gray-800 truncate min-w-0">
                            {communication.title}
                        </p>
                        {lastEntry && preview && PreviewIcon && (
                            <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                <span
                                    className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                                    style={{
                                        background: previewDef?.soft,
                                        color: previewDef?.tone
                                    }}
                                >
                                    <PreviewIcon className="h-2.5 w-2.5" />
                                </span>
                                <span className="text-[12px] text-gray-400 truncate min-w-0">
                                    {preview}
                                </span>
                            </div>
                        )}
                    </>
                );
            }
        },
        {
            key: 'categories',
            label: 'Categories',
            width: widths.categories,
            cellClassName: `${TD_CLIP} py-4 whitespace-nowrap`,
            headerClassName: TH_PADDING,
            render: (communication) => <CategoryPillsCapped categories={communication.categories} />
        },
        {
            key: 'assignee',
            label: 'Assignee',
            width: widths.assignee,
            // Deliberately NOT TD_CLIP: the assignee cell's dropdown is an
            // absolutely-positioned child, and overflow-hidden here would clip it.
            cellClassName: `${TD_PADDING} py-4 whitespace-nowrap`,
            headerClassName: TH_PADDING,
            stopClickPropagation: true,
            render: (communication, index) => (
                <CommunicationAssigneeCell
                    communication={communication}
                    dropUp={index >= communications.length - DROP_UP_ROWS}
                />
            )
        },
        {
            key: 'entries',
            label: 'Interactions',
            width: widths.entries,
            cellClassName: `${TD_CLIP} py-4 whitespace-nowrap`,
            headerClassName: TH_PADDING,
            render: (communication) => <EntriesCell communication={communication} />
        },
        {
            key: 'lastActivity',
            label: 'Last activity',
            width: widths.lastActivity,
            sortable: true,
            sortKey: 'lastActivityAt',
            cellClassName: `${TD_CLIP} py-4 whitespace-nowrap`,
            headerClassName: TH_PADDING,
            render: (communication) => (
                <span className="text-[13px] text-gray-600">
                    {formatRelativeActivity(communication.lastActivityAt) ?? '-'}
                </span>
            )
        },
        {
            key: 'status',
            label: 'Status',
            width: widths.status,
            cellClassName: `${TD_CLIP} py-4 whitespace-nowrap`,
            headerClassName: TH_PADDING,
            render: (communication) => <CommunicationStatusBadge status={communication.status} />
        },
        {
            key: 'rowMenu',
            label: '',
            width: widths.rowMenu,
            cellClassName: `${TD_PADDING} py-4 relative`,
            headerClassName: TH_PADDING,
            stopClickPropagation: true,
            render: (communication) => <RowMenu items={menuItemsFor(communication, onDelete)} />
        }
    ];
}
